import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import APIS from './apis/apis';
const SVGIcon  = lazy(() => import('./assets/svg/SvgIcon'));
const SVGIcon2 = lazy(() => import('./assets/svg/SVGIcon2'));
const SVGIcon3 = lazy(() => import('./assets/svg/SVGIcon3'));
const SVGIcon4 = lazy(() => import('./assets/svg/SVGIcon4'));
const SVGIcon5 = lazy(() => import('./assets/svg/SVGIcon5'));
import MaintenancePage from './components/common/MaintenancePage';
import ErrorBoundary from './components/common/ErrorBoundary';
import { fetchLogo } from './utils/logoCache';
import Splash from './splash/Splash';

import './app.a90ba51f.css';
import './chunk-common.0dc9765b.css';
import './memberCenter.c9fbf00d.css';

// Lazy-loaded routes — each page is only downloaded when first visited
const ForgotPasswordRequest = lazy(() => import('./components/auth/ForgotPasswordRequest'));
const ForgotPasswordUnified = lazy(() => import('./components/auth/ForgotPasswordUnified'));
const ForgotPasswordVerify  = lazy(() => import('./components/auth/ForgotPasswordVerify'));
const MobileAccount         = lazy(() => import('./pages/Mobile/Account'));
const AccountRecord         = lazy(() => import('./pages/Mobile/AccountRecord'));
const BettingRecord         = lazy(() => import('./pages/Mobile/BettingRecord'));
const BiundEwallet          = lazy(() => import('./pages/Mobile/BindEwallet'));
const ChangePassword        = lazy(() => import('./pages/Mobile/ChangePassword'));
const Deposit               = lazy(() => import('./pages/Mobile/Deposit'));
const DepositRecord         = lazy(() => import('./pages/Mobile/DepositRecord'));
const EwalletList           = lazy(() => import('./pages/Mobile/EwalletList'));
const EXHome                = lazy(() => import('./pages/Mobile/Exchange'));
const EXHomeVelki           = lazy(() => import('./pages/Mobile/EXHomeVelki'));
const GameRun               = lazy(() => import('./pages/Mobile/GameRun'));
const NineWiket             = lazy(() => import('./pages/Mobile/NineWiket'));
const Games                 = lazy(() => import('./pages/Mobile/Games'));
const MobileHome            = lazy(() => import('./pages/Mobile/Home'));
const Inbox                 = lazy(() => import('./pages/Mobile/Inbox'));
const Invite                = lazy(() => import('./pages/Mobile/Invite'));
const MobileLogin           = lazy(() => import('./pages/Mobile/Login'));
const MakePayment           = lazy(() => import('./pages/Mobile/MakePayment'));
const Mission               = lazy(() => import('./pages/Mobile/Mission'));
const PersonalInformation   = lazy(() => import('./pages/Mobile/PersonalInformation'));
const ProfitANDLoss         = lazy(() => import('./pages/Mobile/ProfitANDLoss'));
const Promotion             = lazy(() => import('./pages/Mobile/Promotion'));
const RebateBouns           = lazy(() => import('./pages/Mobile/RebetBouns'));
const RewadCenter           = lazy(() => import('./pages/Mobile/RewadCenter'));
const SecurityCenter        = lazy(() => import('./pages/Mobile/SecurityCenter'));
const ShowPromotion         = lazy(() => import('./pages/Mobile/ShowPromotion'));
const MobileSiginUp         = lazy(() => import('./pages/Mobile/SiginUp'));
const Withdrawal            = lazy(() => import('./pages/Mobile/Withdrawals'));
const WithdrawRecord        = lazy(() => import('./pages/Mobile/WithdrawRecord'));
const StoreCode             = lazy(() => import('./StoreCodeComponents'));

// SVG sprite components are lazy-loaded — keeps them out of the main bundle (~4.9 MB)
// Wrapped in Suspense(fallback=null) so icons appear as soon as chunks arrive

function App() {
	const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('token'));
	const [showContent, setShowContent] = useState(false);
	const [pwaIconUrl, setPwaIconUrl] = useState(null);
	const [pwaSiteName, setPwaSiteName] = useState(null);

  // ── /#/9wkt exit handler ──────────────────────────────────────────────────
  useEffect(() => {
    if (window.location.href.includes('/#/9wkt')) {
      const token = localStorage.getItem('token');
      fetch(`${process.env.REACT_APP_API_URL}/api/9wkt/exit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      }).finally(() => {
        const cleanUrl = window.location.href.split('/#/9wkt')[0];
        window.location.replace(cleanUrl);
      });
    }
  }, []);

	const location = useLocation();
	const [globalData, setGlobalData] = useState({});
	const [showModal, setShowModal] = useState(false);
	const [modalMessage, setModalMessage] = useState('');
	const [refreshPvtData, setRefreshPvtData] = useState(0);
	const [color, setColor] = useState(false);


  const triggerPrivateRefresh = () => {
    setRefreshPvtData(prev => prev + 1);
  };

	const showAlert = (message = false) => { 
		if (!message) { 
			setShowModal(false);
			return;
		}
		setShowModal(true);
		setModalMessage(message);
	};
	

  useEffect(() => {
    // Only apply app background when rendering as the actual app view
    // (mobile, or inside the desktop iframe). Do NOT apply on the outer
    // desktop wrapper page — that body should keep its body-bg image/color.
    const isDesktop = !/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const inIframe = window.self !== window.top;
    if (!isDesktop || inIframe) {
      document.body.classList.add('outter-app');
    }
  }, []);

  // Function to dynamically set the favicon
  const setFavicon = (url) => {
    if (!url) return;
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = url;
  };
  // Set favicon from cached logo data (shared fetch, no duplicate network call)
  useEffect(() => {
    fetchLogo().then((data) => {
      if (data?.favicon) {
        const faviconUrl = data.favicon.startsWith('http')
          ? data.favicon
          : `${process.env.REACT_APP_API_URL}/storage/${data.favicon}`;
        setFavicon(faviconUrl);
        // prefer logo field if available, otherwise use favicon for PWA icon
        const pwaIcon = data?.logo
          ? (data.logo.startsWith('http') ? data.logo : `${process.env.REACT_APP_API_URL}/storage/${data.logo}`)
          : faviconUrl;
        setPwaIconUrl(pwaIcon);
      }
    });
  }, []);

  // Sync general data from globalData (already fetched by APIS — no extra request)
  useEffect(() => {
    const g = globalData?.general;
    if (!g) return;
    if (g.site_title) {
      document.title = g.site_title;
      setPwaSiteName(g.site_title);
    }
    if (g.whatsapp)   localStorage.setItem('whatsapp',  g.whatsapp);
    if (g.live_chat)  localStorage.setItem('liveChat',  g.live_chat);
    if (g.intercom)   localStorage.setItem('intercom',  g.intercom);
    if (g.tawk_id)    localStorage.setItem('tawkId',    g.tawk_id);
  }, [globalData]);

  // Dynamically update the PWA manifest once site name and icon are fetched
  useEffect(() => {
    if (!pwaSiteName && !pwaIconUrl) return;
    const manifestData = {
      short_name: pwaSiteName || document.title,
      name: pwaSiteName || document.title,
      start_url: '/',
      display: 'standalone',
      theme_color: '#000000',
      background_color: '#ffffff',
      icons: pwaIconUrl
        ? [
            { src: pwaIconUrl, sizes: '192x192', type: 'image/png' },
            { src: pwaIconUrl, sizes: '512x512', type: 'image/png' },
          ]
        : [],
    };
    const blob = new Blob([JSON.stringify(manifestData)], { type: 'application/json' });
    const manifestUrl = URL.createObjectURL(blob);
    let manifestLink = document.querySelector("link[rel='manifest']");
    if (!manifestLink) {
      manifestLink = document.createElement('link');
      manifestLink.rel = 'manifest';
      document.head.appendChild(manifestLink);
    }
    manifestLink.href = manifestUrl;
    return () => URL.revokeObjectURL(manifestUrl);
  }, [pwaSiteName, pwaIconUrl]);

	useEffect(() => {
    const hostname = (window.location.hostname || '').toLowerCase();
    const origin   = (window.location.origin  || '').toLowerCase();
    const brand    = (process.env.REACT_APP_BRAND || localStorage.getItem('brand') || '').toLowerCase();

    const isJW88      = hostname.includes('jw88')      || origin.includes('jw88')      || brand.includes('jw88');
    const isLocalhost = hostname.includes('localhost')  || origin.includes('localhost')  || brand.includes('localhost');
    const is7xplay    = hostname.includes('7xplay')     || origin.includes('7xplay')     || brand.includes('7xplay');
    const isdk66      = hostname.includes('dk66')       || origin.includes('dk66')       || brand.includes('dk66');
    const isabc2bet   = hostname.includes('abc2bet')    || origin.includes('abc2bet')    || brand.includes('abc2bet');
    const isKrikiya   = hostname.includes('krikiya')    || origin.includes('krikiya')    || brand.includes('krikiya');

    const selectedBrand = isabc2bet ? 'abc2bet' : isdk66 ? 'dk66' : isKrikiya ? 'krikiya' : isJW88 ? 'jw88' : isLocalhost ? 'localhost' : is7xplay ? '7xplay' : 'default';

    localStorage.setItem('brand', selectedBrand);

    // No cache-busting query string — lets the browser cache the CSS file
    const link = document.createElement('link');
    link.rel  = 'stylesheet';
    link.type = 'text/css';
    link.href = `${selectedBrand}.css`;
    document.head.appendChild(link);

    // Inject brand-specific color overrides directly for guaranteed application
    if (isabc2bet) {
      const style = document.createElement('style');
      style.id = 'abc2bet-overrides';
      style.textContent = `
        #root .color1, #root .color3, #root .color4 { color: #ffffff !important; }
        #root .widthMoney, #root .widthId { color: #ffffff !important; }
        #root .withdraw-detail-list .records-root .am-card-body .card-footer .card-item .card-num.amount { color: #ffffff !important; }
        #root .withdraw-detail-list .records-root .am-card-body .card-footer .card-item .card-title { color: rgba(255,255,255,0.75) !important; }
        #root .withdraw-detail-list .records-root .am-card-body .card-body .card-item .card-num { color: #ffffff !important; }
        #root .withdraw-detail-list .records-root .am-card-body .card-body .card-item .card-title { color: rgba(255,255,255,0.75) !important; }
        #root .records-root .remark { color: #ffffff !important; }
      `;
      document.head.appendChild(style);
    }

    setColor(isJW88 || isLocalhost);

    return () => {
      document.querySelectorAll(`link[href*="${selectedBrand}.css"]`).forEach(el => el.remove());
      const overrideStyle = document.getElementById('abc2bet-overrides');
      if (overrideStyle) overrideStyle.remove();
    };
  }, []);


  

  const fetchBalance = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/balance`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('balance', data.balance);
        // Avoid direct DOM manipulation — let React components read from localStorage
      } else if (response.status === 401) {
        handleLogout();
      }
    } catch (_) {
      // silent fail — balance will be refreshed on next poll
    }
  }, []); 
	
	

  const handleLogout = () => {
    localStorage.clear();
    setIsLoggedIn(false);
    window.location.href = '/home';
  };

  // Balance polling — only when logged in AND tab is visible (saves server load)
  useEffect(() => {
    if (!isLoggedIn) return;

    let interval = null;

    const startPolling = () => {
      fetchBalance();
      interval = setInterval(fetchBalance, 30000); // 30s instead of 10s
    };

    const stopPolling = () => {
      if (interval) { clearInterval(interval); interval = null; }
    };

    // Only poll when the browser tab is active/visible
    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        stopPolling(); // clear any existing
        startPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    if (!document.hidden) {
      startPolling();
    }

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isLoggedIn, fetchBalance]);

  // Prefetch all lazy route chunks after splash — eliminates per-click download delay
  useEffect(() => {
    if (!showContent) return;
    const timer = setTimeout(() => {
      import('./pages/Mobile/Login');
      import('./pages/Mobile/SiginUp');
      import('./pages/Mobile/Account');
      import('./pages/Mobile/Deposit');
      import('./pages/Mobile/Withdrawals');
      import('./pages/Mobile/Games');
      import('./pages/Mobile/Promotion');
      import('./pages/Mobile/RewadCenter');
      import('./pages/Mobile/GameRun');
      import('./pages/Mobile/Invite');
      import('./pages/Mobile/AccountRecord');
      import('./pages/Mobile/BettingRecord');
      import('./pages/Mobile/PersonalInformation');
      import('./pages/Mobile/SecurityCenter');
      import('./pages/Mobile/ChangePassword');
      import('./pages/Mobile/Inbox');
      import('./pages/Mobile/MakePayment');
      import('./pages/Mobile/WithdrawRecord');
      import('./pages/Mobile/DepositRecord');
      import('./pages/Mobile/EwalletList');
      import('./pages/Mobile/Exchange');
      import('./pages/Mobile/EXHomeVelki');
      import('./pages/Mobile/ShowPromotion');
      import('./pages/Mobile/Mission');
      import('./pages/Mobile/RebetBouns');
      import('./pages/Mobile/ProfitANDLoss');
      import('./pages/Mobile/BindEwallet');
      import('./pages/Mobile/NineWiket');
      import('./components/auth/ForgotPasswordRequest');
      import('./components/auth/ForgotPasswordUnified');
      import('./components/auth/ForgotPasswordVerify');
      import('./StoreCodeComponents');
    }, 1500);
    return () => clearTimeout(timer);
  }, [showContent]);

  const PrivateRoute = useMemo(() => {
    return function PrivateRouteInner({ element }) {
      return isLoggedIn ? element : <Navigate to="/login" />;
    };
  }, [isLoggedIn]);
const [gtmId, setGtmId] = useState(null);

  useEffect(() => {
    const id = globalData?.general?.google_tag;
    if (!id) return;

    setGtmId(id);

    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
    }

    // ✅ ডুপ্লিকেট স্ক্রিপ্ট থাকলে remove করো
    const oldScript = document.getElementById("gtm-script");
    if (oldScript) oldScript.remove();

    
    const script = document.createElement("script");
    script.id = "gtm-script";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${id}`;
    document.head.appendChild(script);

   
    window.dataLayer.push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });


    // cleanup optional
    return () => {
      const s = document.getElementById("gtm-script");
      if (s) s.remove();
    };
  }, [globalData]);



  // Derive maintenance from already-fetched globalData — no extra network call
  const _g = globalData?.general || {};
  const _generalReady = Object.keys(_g).length > 0;
  const _isMaintenance = _generalReady && String(_g.site_status) === '0';
  const _maintenanceMsg = _g.site_notice || _g.message;

	return (
    <div>
	  {gtmId && (
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
            title="gtm"
          ></iframe>
        </noscript>
      )}
      {/* 🌐 Load initial global + private API data */}
      <APIS setGlobalData={setGlobalData} refreshPvtData={refreshPvtData} />

		{/* Splash overlays for 2s with fade, then reveals content */}
		<Suspense fallback={null}>
			{!showContent && (
				<Splash onSplashComplete={() => setShowContent(true)} />
			)}
		</Suspense>

      {/* 🔷 Global SVG assets — lazy chunks, icons visible as soon as they arrive */}
      <Suspense fallback={null}><SVGIcon /></Suspense>
      <Suspense fallback={null}><SVGIcon3/></Suspense>
      <Suspense fallback={null}><SVGIcon4/></Suspense>
      <Suspense fallback={null}><SVGIcon5/></Suspense>
      <svg className="svg-gradient">
        <linearGradient id="vendor-linear" x1={18} y1="28.5103" x2={18} y2="8.71443" gradientUnits="userSpaceOnUse">
          <stop stopColor="#006540" />
          <stop offset={1} stopColor="#00D47E" />
        </linearGradient>
        <linearGradient id="common-gradient" x1="16.6005" y1="0.0556641" x2="16.6005" y2="11.6957" gradientUnits="userSpaceOnUse">
          <stop stopColor="#DBF1FB" />
          <stop offset="0.455" stopColor="#A6B9C2" />
          <stop offset="0.74" stopColor="#DBF1FB" />
          <stop offset={1} stopColor="#A6B9C2" />
        </linearGradient>
        <linearGradient id="common-gradient-2" x1="20.5" y1="4.5" x2="20.5" y2="35.5" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FFE799" />
          <stop offset="0.333333" stopColor="#FFD753" />
          <stop offset="0.666667" stopColor="#FFE799" />
          <stop offset={1} stopColor="#F59407" />
        </linearGradient>
      </svg>
      <Suspense fallback={null}><SVGIcon2 /></Suspense>

	{/* 🧭 App Routes - render after splash finishes */}
	{/* Maintenance page — uses already-fetched globalData, no extra /api/general request */}
	{showContent && _isMaintenance && (
		<ErrorBoundary>
		<MaintenancePage message={_maintenanceMsg || undefined} />
		</ErrorBoundary>
	)}
	{/* Show routes immediately after splash; hide only if maintenance is confirmed */}
	{showContent && !_isMaintenance && (
		<ErrorBoundary>
		<Suspense fallback={null}>
		<Routes>
				<Route
					path="/forgot-password"
					element={<ForgotPasswordRequest showAlert={showAlert} />}
				/>
				<Route
					path="/forgot-password-unified"
					element={<ForgotPasswordUnified showAlert={showAlert} />}
				/>
				<Route
					path="/forgot-password/verify"
					element={<ForgotPasswordVerify showAlert={showAlert} />}
				/>
        <Route
          path="/home"
						element={<MobileHome
									globalData={globalData}
									sociallink={globalData.socialLinks || {}}
									triggerPrivateRefresh={triggerPrivateRefresh}
								/>}
        />
					<Route
						path="/login"
						element={<MobileLogin />}
					/>
					<Route
						path="/signup"
						element={<MobileSiginUp />}
					/>
       	<Route
					path="/member"
					element={
					<PrivateRoute
						element={
						<MobileAccount
							refreshPrivateData={triggerPrivateRefresh}
							userData={globalData.userData}
							general={globalData.general || {}}          
							sociallink={globalData.socialLinks || {}}  
							
						/>
						}
					/>
					}
				/>
        	<Route
					path="/rewardcenter"
					element={
					<PrivateRoute
						element={
						<RewadCenter
							refreshPrivateData={triggerPrivateRefresh}
							userData={globalData.userData}
							general={globalData.general || {}}          
							sociallink={globalData.socialLinks || {}}  
						/>
						}
					/>
					}
				/>
        <Route
					path="/bettingRecord"
					element={
					<PrivateRoute
						element={
						<BettingRecord
						
						/>
						}
					/>
					}
				/>
				<Route
					path="/games"
					element={
					<PrivateRoute
						element={
						<Games
						
						/>
						}
					/>
					}
				/>
				  <Route
					path="/activityDetail"
					element={
					<PrivateRoute
						element={
						<ShowPromotion
						
						/>
						}
					/>
					}
				/>
				
         <Route
					path="/profitandloss"
					element={
					<PrivateRoute
						element={
						<ProfitANDLoss
						
						/>
						}
					/>
					}
				/>
        <Route
					path="/depositRecord"
					element={
					<PrivateRoute
						element={
						<DepositRecord
						 transactionData={globalData.transactions || [] }
						/>
						}
					/>
					}
				/>
         <Route
					path="/withdrawRecord"
					element={
					<PrivateRoute
						element={
						<WithdrawRecord
						 transactionData={globalData.transactions || [] }
						
						/>
						}
					/>
					}
				/>
         <Route
					path="/transactionRecord"
					element={
					<PrivateRoute
						element={
						<AccountRecord
						 transactionData={globalData.transactions || [] }
						/>
						}
					/>
					}
				/>
        
         <Route
					path="/personalInformation"
					element={
					<PrivateRoute
						element={
						<PersonalInformation
						userData={globalData.userData}
						/>
						}
					/>
					}
				/>
          <Route
					path="/changePassword"
					element={
					<PrivateRoute
						element={
						<ChangePassword
						showAlert={showAlert}
						
						/>
						}
					/>
					}
				/>
				<Route
					path="/securityCenter"
					element={
					<PrivateRoute
						element={
							
						<SecurityCenter
						showAlert={showAlert}
						userData={globalData.userData}
						
						/>
						}
					/>
					}
				/>
				<Route
					path="/ewalletList"
					element={
					<PrivateRoute
						element={
						<EwalletList
						showAlert={showAlert}
						
						/>
						}
					/>
					}
				/>
				<Route
					path="/addEwallet"
					element={
					<PrivateRoute
						element={
						<BiundEwallet
						showAlert={showAlert}
						
						/>
						}
					/>
					}
				/>
         <Route
					path="/referral"
					element={
					<PrivateRoute
						element={
						<Invite
						showAlert={showAlert} userData={globalData.userData}
						/>
						}
					/>
					}
				/>
         <Route
					path="/mission"
					element={
					<PrivateRoute
						element={
						<Mission
							turnovers={globalData.turnovers || []}
							userData={globalData.userData || {}}
						/>
						}
					/>
					}
				/>
         <Route
					path="/rebateBonus"
					element={
					<PrivateRoute
						element={
						<RebateBouns
						
						/>
						}
					/>
					}
				/>
				 <Route
					path="/exchange"
					element={
					<PrivateRoute
						element={
						<EXHome
						
						/>
						}
					/>
					}
				/>
				<Route
					path="/9wiket"
					element={<NineWiket />}
				/>
				<Route
					path="/velki"
					element={
					<PrivateRoute
						element={<EXHomeVelki />}
					/>
					}
				/>
				<Route path="/make-payment" element={<MakePayment hasEWallet={globalData.hasEWallet} userData={globalData.userData || [] } />} />
				<Route path="/referral_code" element={<StoreCode codeType="referral_code" />} />
				<Route path="/affiliate_code" element={<StoreCode codeType="affiliate_code" />} />
        <Route path="/agent_code" element={<StoreCode codeType="agent_code" />} />
         <Route
					path="/deposit"
					element={
					<PrivateRoute
						element={
						<Deposit
						usdtRate={globalData?.general?.usdt_rate} hasEWallet={globalData.hasEWallet} userData={globalData.userData || [] } transactionData={globalData.transactions || [] } items={globalData.paymentMethods || []}  promotions={globalData.promotions?.activePromotion || []}
						/>
						}
					/>
					}
				/>
         <Route
					path="/Withdraw"
					element={
					<PrivateRoute
						element={
						<Withdrawal
						general={globalData.general} hasEWallet={globalData.hasEWallet} userData={globalData.userData || [] } transactionData={globalData.transactions || [] } items={globalData.paymentMethods || []}
						/>
						}
					/>
					}
				/>
				 <Route
					path="/gamerun"
					element={
					<PrivateRoute
						element={
						<GameRun
						
						/>
						}
					/>
					}
				/>
				 <Route
					path="/promotion"
					element={
					<PrivateRoute
						element={
						<Promotion
						 promotions={globalData.promotions?.allPromotions || []}
						
						/>
						}
					/>
					}
				/>
				 <Route
					path="/inbox"
					element={
					<PrivateRoute
						element={
						<Inbox
						
						
						/>
						}
					/>
					}
				/>
				<Route path="*" element={<Navigate to="/home" />} />
		</Routes>
		</Suspense>
		</ErrorBoundary>
	)}
    </div>
  );
}

export default App;
